import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { PortfolioViewMode } from '../../helpers';

interface PortfolioViewToggleProps {
  value: PortfolioViewMode;
  onChange: (mode: PortfolioViewMode) => void;
}

/**
 * Segmented control that switches the Current holdings table between
 * per-case figures (Cost / Value / P&L per case) and totals-per-wine figures.
 * Flipping the mode changes which cost/value/P&L columns are visible.
 */
const PortfolioViewToggle: FC<PortfolioViewToggleProps> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const isCase = value !== 'total';

  const options: { mode: PortfolioViewMode; label: string }[] = [
    { mode: 'case', label: t('portfolio:view.pricePerCase', 'Price per case') },
    { mode: 'total', label: t('portfolio:view.totalsPerWine', 'Totals per wine') },
  ];

  return (
    <div className="relative flex flex-shrink-0 items-center rounded-full bg-gray-100 dark:bg-slate-800 p-1">
      <span
        aria-hidden
        className={`absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-full bg-orange dark:bg-teal-500 transition-transform duration-200 ease-out ${
          isCase ? 'translate-x-0' : 'translate-x-[calc(100%+0px)]'
        }`}
      />
      {options.map((option) => {
        const active = option.mode === value;
        return (
          <button
            key={option.mode}
            type="button"
            onClick={() => onChange(option.mode)}
            className={`relative z-10 px-3 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap transition-colors ${
              active ? 'text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

export default PortfolioViewToggle;
